<template>
  <CommonPages id="explore" :class="['explore']" :titlePrefix="pageMetaTitle"
    :metaDescription="pageMetaDescription">
    <section class="explore-collections__hero" :style="heroBackgroundStyle">
      <div class="explore-collections__hero-overlay"></div>
      <div class="explore-collections__hero-top"></div>
      <div class="explore-collections__hero-bottom"></div>
      <div class="explore-collections__hero-content">
        <h1 class="explore-collections__hero-title">{{ heroTitle }}</h1>
        <p class="explore-collections__hero-subtitle">{{ heroSubtitle }}</p>
      </div>
    </section>

    <div v-if="collectionsAds.heroTitleBelow" class="explore-collections__section-ad explore-collections__section-ad--hero">
      <AdComponent class="ad-width-100" :ads="collectionsAds.heroTitleBelow" :showDebug="showDebug"
        :forceDebug="true" debugLabel="collection_1"
        debugPosition="Collections Hero 下方" />
    </div>

    <section class="explore-collections__list">
      <template v-for="(group, index) in cityGroups">
        <article :key="group.key" class="explore-collections__group">
          <div class="explore-collections__group-info">
            <div class="explore-collections__group-text">
              <h2 class="explore-collections__group-title">{{ group.title }}</h2>
              <p class="explore-collections__group-desc">{{ group.description }}</p>
            </div>
            <a :href="getCollectionUrl(group.key)" class="explore-collections__group-btn">
              <img src="@/assets/default/star-icon.svg" alt="star" />
              <span>View All</span>
            </a>
          </div>

          <div class="explore-collections__cards">
            <a v-for="city in group.cities" :key="city.slug" :href="getCityUrl(city.slug)"
              class="explore-collections__card">
              <img class="explore-collections__card-image" :src="getCityImage(city.slug)"
                :alt="city.name" />
              <div class="explore-collections__card-shadow"></div>
              <div class="explore-collections__card-name">{{ city.name }}</div>
              <div class="explore-collections__card-score">
                <strong>{{ city.score }}</strong>
                <em>Overall</em>
              </div>
            </a>
          </div>
        </article>
        <div v-if="index < 2 && getGroupAd(index)" :key="`collections-inline-${group.key}`"
          class="explore-collections__section-ad explore-collections__section-ad--between">
          <AdComponent class="ad-width-100" :ads="getGroupAd(index)" :showDebug="showDebug"
            :forceDebug="true" :debugLabel="`collection_${index + 2}`"
            :debugPosition="`Collections 第${index + 1}个合集模块下方`" />
        </div>
      </template>
    </section>

    <section class="explore-collections__insights">
      <div class="explore-collections__insights-title-wrap">
        <h2 class="explore-collections__insights-title">{{ insightsTitle }}</h2>
        <p class="explore-collections__insights-desc">
          {{ insightsIntro }}
        </p>
      </div>

      <div class="explore-collections__insights-table">
        <div v-for="item in insightsRows" :key="item.title" class="explore-collections__insights-row">
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
        </div>
      </div>
    </section>
  </CommonPages>
</template>

<script>
import { mapGetters } from 'vuex';
import CommonPages from '@/pages/components/page_common.vue';
import AdComponent from '@/pages/components/AdComponent.vue';
import adMixin from '@/mixin/adMixin.js';
import adTrackingMixin from '@/mixin/adTrackingMixin.js';
import { getContentTextMap, getText } from '@/utils/contentText';
import { getRenderableAd } from '@/utils/adConfig';
const COLLECTIONS_HERO_IMAGE =
  'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=2400&q=80';

const COLLECTION_GROUP_KEYS = ['airQuality', 'safety', 'livingCost'];

const COLLECTION_COPY = {
  groups: [
    { title: 'Smart Living', description: 'High-efficiency living on a balanced budget. These cities offer top-tier transit systems without the premium price tag.' },
    { title: 'Pure Comfort', description: 'Focus on your well-being. We’ve selected destinations with the cleanest air and the most secure environments for your peace of mind.' },
    { title: 'Core Quality', description: 'Where world-class medical care meets seamless mobility. Experience the highest standards of public services and healthcare.' }
  ],
  insightsIntro: 'Selecting a home requires precision. Our data-driven framework categorizes urban performance into three distinct tiers, offering a transparent look at the living standards within each global destination.',
  insights: [
    { title: 'Air Quality', description: 'Excellent reflects superior atmospheric purity, Good denotes consistent healthy air, and Moderate indicates areas with occasional environmental challenges.' },
    { title: 'Safety', description: 'Very High represents a global gold standard in security, High ensures a stable social climate, and Medium suggests standard urban vigilance.' },
    { title: 'Living Cost', description: 'Low provides maximum economic accessibility, Medium balances quality with cost, and High signifies a premium investment in lifestyle.' },
    { title: 'Healthcare', description: 'Excellent delivers elite medical infrastructure, Good provides dependable health services, and Basic maintains fundamental clinical support for residents.' },
    { title: 'Transport', description: 'Excellent enables a fully walkable urban experience, Good features efficient public networks, and Moderate provides essential transit connectivity.' }
  ]
};

const COLLECTION_GROUP_TEXT_KEYS = [
  { titleKey: 'CARD1_TITLE', descriptionKey: 'CARD1_DESC' },
  { titleKey: 'CARD2_TITLE', descriptionKey: 'CARD2_DESC' },
  { titleKey: 'CARD3_TITLE', descriptionKey: 'CARD3_DESC' }
];

const INSIGHT_TEXT_KEYS = [
  { title: 'Air Quality', descriptionKey: 'INSIGHTS_AIR_QUALITY' },
  { title: 'Safety', descriptionKey: 'INSIGHTS_SAFETY' },
  { title: 'Living Cost', descriptionKey: 'INSIGHTS_LIVING_COST' },
  { title: 'Healthcare', descriptionKey: 'INSIGHTS_HEALTHCARE' },
  { title: 'Transport', descriptionKey: 'INSIGHTS_TRANSPORT' }
];

export default {
  name: 'CollectionsPage',
  components: {
    CommonPages,
    AdComponent
  },
  mixins: [adMixin, adTrackingMixin],
  computed: {
    ...mapGetters({
      domainConfig: 'domainConfig',
      adsensConfig: 'adsensConfig',
      showDebug: 'showDebug',
      livableCityData: 'livableCityData',
      deviceType: 'deviceType'
    }),
    cityGroups () {
      return COLLECTION_COPY.groups.map((group, index) => {
        const textKeys = COLLECTION_GROUP_TEXT_KEYS[index];
        return {
          key: COLLECTION_GROUP_KEYS[index],
          title: getText(this.contentText, textKeys.titleKey, group.title),
          description: getText(this.contentText, textKeys.descriptionKey, group.description),
          cities: this.getCitiesByStandard(COLLECTION_GROUP_KEYS[index])
        };
      });
    },
    insightsTitle () {
      return getText(this.contentText, 'INSIGHTS_TITLE', 'INSIGHTS');
    },
    insightsIntro () {
      return getText(this.contentText, 'INSIGHTS_DESC', COLLECTION_COPY.insightsIntro);
    },
    insightsRows () {
      return COLLECTION_COPY.insights.map((item, index) => ({
        title: INSIGHT_TEXT_KEYS[index].title,
        description: getText(this.contentText, INSIGHT_TEXT_KEYS[index].descriptionKey, item.description)
      }));
    },
    collectionsAds () {
      const cfg = this.adsensConfig || {};
      return {
        heroTitleBelow: getRenderableAd(cfg.collection_1),
        firstGroupAfter: getRenderableAd(cfg.collection_2),
        secondGroupAfter: getRenderableAd(cfg.collection_3),
      };
    },
    heroBackgroundStyle () {
      return {
        backgroundImage: `url(${COLLECTIONS_HERO_IMAGE})`
      };
    },
    contentText () {
      return getContentTextMap(this.domainConfig);
    },
    pageMetaTitle () {
      return getText(this.contentText, 'COLLECTIONS_META_TITLE', this.heroTitle);
    },
    heroTitle () {
      return 'Curated Collections';
    },
    heroSubtitle () {
      return 'A Place to Call Home: The Definitive Livable Cities List';
    },
    pageMetaDescription () {
      return getText(this.contentText, 'COLLECTIONS_META_DESCRIPTION', 'Explore 50 livable cities around the world.');
    }
  },
  methods: {
    getGroupAd (index) {
      const map = [this.collectionsAds.firstGroupAfter, this.collectionsAds.secondGroupAfter];
      return map[index] || null;
    },
    getCityImage (slug) {
      const matched = (this.livableCityData.cities || []).find((city) => city.slug === slug);
      return matched ? matched.image : '';
    },
    getCityUrl (slug) {
      const target = `/${slug}`;
      if (this.$route.params.channel) {
        return `/${this.$route.params.channel}${target}`;
      }
      return target;
    },
    getCollectionUrl (key) {
      const target = `/cities?standard=${key || 'airQuality'}`;
      if (this.$route.params.channel) {
        return `/${this.$route.params.channel}${target}`;
      }
      return target;
    },
    getStableSortValue (city, standardKey) {
      const seed = `${standardKey}-${city.slug}`;
      let hash = 0;
      for (let i = 0; i < seed.length; i += 1) {
        hash = (hash * 31 + seed.charCodeAt(i)) % 100000;
      }
      return hash;
    },
    getCitiesByStandard (standardKey) {
      const source = this.livableCityData.cities || [];
      const topValue = this.livableCityData.topStandardValue?.[standardKey];
      const pool = source
        .filter((city) => city.metrics[standardKey] === topValue)
        .sort((a, b) => this.getStableSortValue(a, standardKey) - this.getStableSortValue(b, standardKey));
      return pool.slice(0, 6);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.explore-collections {
	&__hero {
		position: relative;
		height: rem(400);
		background-size: cover;
		background-position: center;
		overflow: hidden;
	}

	&__header-ad {
		padding: rem(60) rem(20) 0;
		box-sizing: border-box;
		background: #fff7e1;
	}

	&__hero-overlay,
	&__hero-bottom,
	&__hero-top {
		position: absolute;
		left: 0;
		right: 0;
		pointer-events: none;
	}

	&__hero-overlay {
		top: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.3);
	}

	&__hero-top {
		top: 0;
		height: rem(120);
		background: linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 49.88%, rgba(0, 0, 0, 0) 100%);
	}

	&__hero-bottom {
		bottom: 0;
		height: rem(220);
		background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.4) 49.88%, rgba(0, 0, 0, 0.6) 100%);
	}

	&__hero-content {
		position: relative;
		z-index: 2;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: rem(24);
		padding-top: rem(88);
		text-align: center;
		text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
		color: #fff;
	}

	&__hero-ad {
		width: 100%;
		padding: 0 rem(120);
		box-sizing: border-box;
	}

	&__hero-title {
		font-family: $font-family-serif;
		font-size: rem(52);
		font-weight: 700;
		line-height: rem(60);
		margin: 0;
	}

	&__hero-subtitle {
		font-family: $font-family-serif;
		font-size: rem(28);
		font-weight: 500;
		line-height: rem(36);
		margin: 0;
	}

	&__list {
		background: #fff7e1;
		padding: rem(40) rem(180) 0;
		display: flex;
		flex-direction: column;
		gap: rem(40);
	}

	&__section-ad {
		padding: rem(16) rem(180) 0;
		box-sizing: border-box;
		background: #fff7e1;
	}

	&__section-ad--first-group {
		padding-left: 0;
		padding-right: 0;
	}

	&__insights-ad {
		margin: rem(16) 0;
	}

	&__group {
		border-radius: rem(24);
		border: 1px solid #fecd45;
		background: linear-gradient(270deg, rgba(254, 205, 69, 0.2) 0%, rgba(254, 205, 69, 0) 100%);
		box-shadow: 0 rem(4) rem(4) rgba(254, 205, 69, 0.24);
		padding: rem(24);
		display: flex;
		gap: rem(24);
	}

	&__group-info {
		flex: 1;
		min-width: rem(260);
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: rem(40) rem(60) rem(20) rem(32);
		gap: rem(28);
	}

	&__group-text {
		display: flex;
		flex-direction: column;
		gap: rem(40);
	}

	&__group-title {
		margin: 0;
		color: #000;
		font-family: $font-family-serif;
		font-size: rem(32);
		font-weight: 700;
		line-height: rem(32);
	}

	&__group-desc {
		margin: 0;
		color: #000;
		font-family: $font-family-serif;
		font-size: rem(18);
		font-weight: 300;
		line-height: rem(28);
	}

	&__group-btn {
		width: rem(200);
		height: rem(52);
		border-radius: rem(100);
		background: #000;
		color: #fecd45;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: rem(12);
		text-decoration: none;

		img {
			width: rem(24);
			height: rem(24);
		}

		span {
			font-family: $font-family-serif;
			font-size: rem(22);
			font-weight: 700;
			line-height: rem(24);
		}
	}

	&__cards {
		width: rem(636);
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: rem(24);
	}

	&__card {
		position: relative;
		display: block;
		width: rem(196);
		height: rem(196);
		border-radius: rem(24);
		overflow: hidden;
		text-decoration: none;
		border: 1px solid rgba(0, 0, 0, 0.1);
	}

	&__card-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__card-shadow {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: rem(72);
		background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 80%);
	}

	&__card-name {
		position: absolute;
		left: rem(16);
		bottom: rem(16);
		color: #fff;
		font-family: $font-family-serif;
		font-size: rem(20);
		font-weight: 700;
		line-height: rem(20);
		text-shadow: 0 rem(2) rem(4) rgba(0, 0, 0, 0.16);
	}

	&__card-score {
		position: absolute;
		top: rem(8);
		left: rem(8);
		width: rem(52);
		border-radius: rem(20) rem(12) rem(12) rem(12);
		background: #fecd45;
		padding: rem(8);
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: rem(4);

		strong {
			color: #000;
			font-family: $font-family-serif;
			font-size: rem(16);
			font-weight: 700;
			line-height: rem(16);
		}

		em {
			color: #000;
			font-family: $font-family-serif;
			font-size: rem(12);
			font-style: italic;
			line-height: rem(12);
		}
	}

	&__insights {
		background: #fff7e1;
		padding: rem(80) rem(180);
		display: flex;
		flex-direction: column;
		gap: rem(40);
	}

	&__insights-title-wrap {
		padding: 0 rem(224);
		text-align: center;
		display: flex;
		flex-direction: column;
		gap: rem(12);
	}

	&__insights-title {
		margin: 0;
		color: #000;
		font-family: $font-family-serif;
		font-size: rem(28);
		font-weight: 700;
		line-height: rem(36);
	}

	&__insights-desc {
		margin: 0;
		color: #000;
		font-family: $font-family-serif;
		font-size: rem(16);
		font-weight: 300;
		line-height: rem(24);
	}

	&__insights-table {
		display: flex;
		flex-direction: column;
	}

	&__insights-row {
		display: flex;
		align-items: center;
		gap: rem(80);
		padding: rem(32) rem(24);
		border-top: 1px solid rgba(0, 0, 0, 0.1);

		&:last-child {
			border-bottom: 1px solid rgba(0, 0, 0, 0.1);
		}

		h3 {
			margin: 0;
			width: rem(120);
			color: #000;
			font-family: $font-family-serif;
			font-size: rem(20);
			font-weight: 700;
			line-height: rem(20);
		}

		p {
			margin: 0;
			flex: 1;
			color: #000;
			font-family: $font-family-serif;
			font-size: rem(16);
			font-weight: 300;
			line-height: rem(24);
		}
	}
}

.explore__ad {
	background: #fff7e1;
	padding: rem(16) rem(20) 0;
	box-sizing: border-box;
}

.detect_deviece_mobile {
	.explore-collections {
		&__hero {
			height: rem(260);
		}

		&__header-ad {
			padding: rem(60) rem(16) 0;
		}

		&__hero-top {
			height: rem(90);
		}

		&__hero-bottom {
			height: rem(120);
		}

		&__hero-content {
			gap: rem(10);
			padding: rem(72) rem(20) 0;
		}

		&__hero-ad {
			padding: 0 rem(16);
		}

		&__hero-title {
			font-size: rem(32);
			line-height: rem(36);
		}

		&__hero-subtitle {
			font-size: rem(18);
			line-height: rem(24);
		}

		&__list {
			padding: rem(20) rem(16) 0;
			gap: rem(20);
		}

		&__section-ad {
			padding: rem(12) rem(16) 0;
		}

		&__section-ad--between {
			padding: 0;
		}
		&__section-ad--first-group {
			padding-left: 0;
			padding-right: 0;
		}

		&__group {
			padding: rem(16);
			flex-direction: column;
			gap: rem(16);
		}

		&__group-info {
			width: 100%;
			min-width: 0;
			padding: rem(8) rem(8) 0;
			gap: rem(20);
		}

		&__group-text {
			gap: rem(12);
		}

		&__group-title {
			font-size: rem(24);
			line-height: rem(24);
		}

		&__group-desc {
			font-size: rem(14);
			line-height: rem(22);
		}

		&__group-btn {
			width: rem(130);
			height: rem(36);

			img {
				width: rem(16);
				height: rem(16);
			}

			span {
				font-size: rem(15);
				line-height: rem(18);
			}
		}

		&__cards {
			display: flex;
			flex-wrap: wrap;
			align-content: flex-start;
			width: 100%;
			min-width: 0;
			gap: rem(12);
		}

		&__card {
			width: calc((100% - #{rem(12)}) / 2);
			height: auto;
			flex: none;
			&::before {
				content: '';
				display: block;
				padding-top: 100%;
			}
		}

		&__card-image {
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
		}

		&__card-name {
			left: rem(12);
			bottom: rem(12);
			font-size: rem(16);
			line-height: rem(16);
		}

		&__card-score {
			width: rem(46);
			padding: rem(6);

			strong {
				font-size: rem(14);
				line-height: rem(14);
			}

			em {
				font-size: rem(10);
				line-height: rem(10);
			}
		}

		&__insights {
			padding: rem(40) rem(16);
			gap: rem(20);
		}

		&__insights-title-wrap {
			padding: 0;
		}

		&__insights-title {
			font-size: rem(24);
			line-height: rem(28);
		}

		&__insights-desc {
			font-size: rem(14);
			line-height: rem(22);
		}

		&__insights-row {
			flex-direction: column;
			align-items: flex-start;
			gap: rem(10);
			padding: rem(20) rem(8);

			h3 {
				width: 100%;
				font-size: rem(18);
				line-height: rem(18);
			}

			p {
				font-size: rem(14);
				line-height: rem(22);
			}
		}
	}

	.explore__ad {
		padding: rem(12) rem(16) 0;
	}
}

.detect_deviece_pc,
.detect_deviece_desktop {
	.explore-collections {
		&__header-ad {
			padding: rem(60) rem(180) 0;
		}
	}

	.explore__ad {
		padding: rem(20) rem(180) 0;
	}
}
</style>
